import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  Row,
} from "reactstrap";
import DeleteModal from "../../components/common/deleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import BreadCrumb from "../../components/common/breadCrumb";
import Widgets from "../dashboard/widgets";
import {
  multiCount,
  supportEmployeeData,
  updateStatusOfSupport,
  viewSupport,
} from "../../api/supportApi";
import TableContainer from "../../components/common/tableContainer";
import Loader from "../../components/common/loader";
import { getId, getRole } from "../../utils/helpers/custom";
import { checkStatusCodeSuccess, CommonLabel, dateFormat, HandleResponse, Roles, TableSortFunction } from "../../utils/helpers/common";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ApproveRejectModal from "../../components/common/approveRejectModal";
import { ResponseStatusEnum, SingleOptions } from "../../utils/helpers/enums";
import { toast } from "react-toastify";
import NoResultsMessage from "../../components/noResultMessage";
import { StatusConfirmation, SupportLabel } from "../../common/constant/Support";
import { ResourceStatus } from "../../common/constant/Resource";
import { InputPlaceHolder, SelectPlaceHolder, validationMessages } from "../../utils/helpers/validationMasseges";
import { BaseSelect } from "../../components/BaseComponents/BaseSelect";
import BaseInput from "../../components/BaseComponents/BaseInput";
import BaseModal from "../../components/BaseComponents/BaseModal";
import { Link } from "react-router-dom";
import { listSupportHR } from "../../api/dashboardApi";

const Status = ({ status }) => {
  switch (status) {
    case SupportLabel.OnProcess:
      return (
        <span className="badge badge-soft-info text-uppercase">{status}</span>
      );
    case CommonLabel.Pending:
      return (
        <span className="badge badge-soft-warning text-uppercase">
          {status}
        </span>
      );
    case SupportLabel.Completed:
      return (
        <span className="badge badge-soft-success text-uppercase">
          {status}
        </span>
      );
    default:
      return (
        <span className="badge badge-soft-success text-uppercase">
          {status}
        </span>
      );
  }
};

const EmployeeSupport = () => {
  document.title = SupportLabel.EmployeeSupport;
  const id = getId("id");
  const role = getRole();

  const [hrSupportList, setHrSupportList] = useState([]);
  const [multipartCount, setMultiPartCount] = useState();
  const [updateModal, setUpdateModal] = useState(false);
  const [updateOnProcessModal, setUpdateOnProcessModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [approvedId, setApprovedId] = useState();
  const [onProcessId, setOnProcessId] = useState();
  const [onProcessAuthId, setOnProcessAuthId] = useState();
  const [modalLoader, setModalLoader] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);

  const fetchData = () => {
    setTableLoader(true);
    listSupportHR({ order: ["id", "DESC"] })
      .then((resp) => {
        setHrSupportList(resp?.data);
      })
      .catch((err) => err);
    multiCount(id)
      .then((response) => {
        setMultiPartCount(response?.data?.data);
      })
      .catch((err) => err)
      .finally(() => {
        setTableLoader(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const QueryColumns = useMemo(() => [
    {
      Header: SupportLabel.EmployeeName,
      accessor: "employeeName",
      Cell: ({ cell }) => {
        return (
          <div>
            {cell?.row?.original?.employeeName}
          </div>
        );
      },
    },
    {
      Header: SupportLabel.Issue,
      accessor: "issue",
      filterable: false,
    },
    {
      Header: SupportLabel.HrName,
      accessor: "assignedUser",
      Cell: ({ cell }) => {
        return (
          <div>
            {cell?.row?.original?.assignedUser}
          </div>
        );
      },
    },
    {
      Header: SupportLabel.DateOfRequest,
      Cell: (items) => {
        return <div>{dateFormat(items?.row?.original?.dateOfRequest)}</div>;
      },
    },
    {
      Header: CommonLabel.Status,
      accessor: "status",
      filterable: false,
      Cell: (cell) => {
        return <Status status={cell?.value} />;
      },
    },
    {
      Header: CommonLabel.Action,
      Cell: (item) => {
        return (
          <div>
            {item.cell.row.original.status !== SupportLabel.Completed ? (
              <div className="hstack gap-2">
                <Link
                  id={`modalTodo-${item?.cell?.row?.index}`}
                  className="btn btn-sm btn-soft-success edit-list"
                  onClick={() => {
                    handleApprove(
                      item?.cell?.row?.original,
                      item?.cell?.row?.original
                    );
                  }}
                >
                  <i className="ri-check-line align-bottom" />
                </Link>
                <ReactTooltip
                  place="bottom"
                  variant="success"
                  content={CommonLabel.Approve}
                  anchorId={`modalTodo-${item?.cell?.row?.index}`}
                />
                <Link
                  id={`deleteTodo-${item?.cell?.row?.index}`}
                  className="btn btn-sm btn-soft-warning remove-list"
                  disabled={
                    item.cell.row.original.status === SupportLabel.OnProcess
                  }
                  onClick={() => {
                    handleOnProcess(
                      item?.cell?.row?.original,
                      item?.cell?.row?.original
                    );
                  }}
                >
                  <i class="ri-arrow-down-circle-line"></i>
                </Link>
                <ReactTooltip
                  place="bottom"
                  variant="warning"
                  content={SupportLabel.OnProcess}
                  anchorId={`deleteTodo-${item?.cell?.row?.index}`}
                />
              </div>
            ) : (
              HandleResponse.NoData
            )}
          </div>
        );
      },
    },
  ]);

  const handleApprove = (row, authApprovedId) => {
    setSelectedRow({ ...row, action: 'approve support query' });
    setUpdateModal(true);
    setApprovedId(row);
  };

  const handleApproveSupport = () => {
    setModalLoader(true);
    updateStatusOfSupport({ id: approvedId.id,  userId: onProcessAuthId?.authUser?.id, status: "Completed",supportIssue: onProcessAuthId?.supportIssue}).then((response) => {
      if (response?.data?.statusCode === ResponseStatusEnum.SUCCESS) {
        toast.success(response?.data?.message);
        fetchData();
        setUpdateModal(false);
      } else {
        toast.error(response?.data?.message);
      }
    }).catch((err) => {
      return err;
    }).finally(() => {
      setModalLoader(false);
    })
    setSelectedRow(null);
  };

  const handleOnProcess = (row, authId) => {
    setSelectedRow({ ...row, action: 'onprocess support query' });
    setUpdateOnProcessModal(true);
    setOnProcessId(row);
    setOnProcessAuthId(authId)
  }

  const handleOnProcessSupport = () => {
    setModalLoader(true);
    updateStatusOfSupport({ id: onProcessId.id, userId: onProcessAuthId?.authUser?.id, status: ResourceStatus.OnProcess ,supportIssue: onProcessAuthId?.supportIssue}).then((response) => {
      if (checkStatusCodeSuccess(response?.data?.statusCode)) {
        toast.success(response?.data?.message);
        fetchData();
        setUpdateOnProcessModal(false);
      } else {
        toast.error(response?.data?.message);
      }
    }).catch((err) => {
      return err;
    }).finally(() => {
      setModalLoader(false);
    })
    setSelectedRow(null);
  };

  const [deleteModal, setDeleteModal] = useState(false);

  const QueryTicket = [
    {
      id: 1,
      cardColor: "primary",
      label: SupportLabel.OpenTickets,
      badge: "ri-arrow-right-up-line",
      badgeClass: "success",
      percentage: multipartCount?.openTicketOfCurrentMonthData,
      counter: multipartCount?.openTicketData,
      bgcolor: "success",
      icon: "ri-account-box-fill",
      decimals: 0,
      prefix: "",
      suffix: "",
    },
    {
      id: 2,
      cardColor: "primary",
      label: SupportLabel.ClosedTickets,
      badge: "ri-arrow-right-up-line",
      badgeClass: "success",
      percentage: multipartCount?.closeTicketOfCurrentMonthData,
      counter: multipartCount?.closeTicketData,
      bgcolor: "success",
      icon: "ri-team-fill",
      decimals: 0,
      prefix: "",
      suffix: "",
    },
    {
      id: 3,
      cardColor: "primary",
      label: SupportLabel.InProgressTickets,
      badge: "ri-arrow-right-up-line",
      badgeClass: "success",
      percentage: multipartCount?.inProgressTicketOfCurrentMonthData,
      counter: multipartCount?.inProgressTicketData,
      bgcolor: "success",
      icon: "ri-file-list-3-fill",
      decimals: 0,
      prefix: "",
      suffix: "",
    },
    //---------------------------- its a important for future---------------------------
    {
      id: 4,
      cardColor: "primary",
      label: SupportLabel.TotalTickets,
      badge: "ri-arrow-right-up-line",
      badgeClass: "success",
      percentage: multipartCount?.totalTicketOfCurrentMonthData,
      counter: multipartCount?.totalTicketData,
      bgcolor: "success",
      icon: "ri-file-list-3-fill",
      decimals: 0,
      prefix: "",
      suffix: "",
    },
    //---------------------------- its a important for future---------------------------
  ];

  const [todo, setTodo] = useState(null);
  const [modalTodo, setModalTodo] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const toggle = useCallback(() => {
    if (modalTodo) {
      setModalTodo(false);
      setTodo(null);
    } else {
      setModalTodo(true);
    }
  }, [modalTodo]);

  const handleDeleteTodo = () => {
    if (todo) {
      setDeleteModal(false);
    }
  };

  let message = "";
  if (selectedRow) {
    if (selectedRow.action === "approve") {
      message = StatusConfirmation(CommonLabel.Approve, selectedRow?.reason);
    } else if (selectedRow.action === "reject") {
      message = StatusConfirmation(CommonLabel.Reject, selectedRow?.reason);
    }
  }

  return (
    <React.Fragment>

      <ApproveRejectModal
        show={updateModal}
        onDeleteClick={() => handleApproveSupport()}
        onCloseClick={() => setUpdateModal(false)}
        message={message}
        action={selectedRow ? selectedRow.action : ""}
        loader={modalLoader}
      />

      <ApproveRejectModal
        show={updateOnProcessModal}
        onDeleteClick={() => handleOnProcessSupport()}
        onCloseClick={() => setUpdateOnProcessModal(false)}
        message={message}
        action={selectedRow ? selectedRow.action : ""}
        loader={modalLoader}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => handleDeleteTodo()}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
          <Container fluid>
            <BreadCrumb title={SupportLabel.Support} pageTitle="HRMS" />
            <Row className="mb-4">
              <div className="col-sm order-3 order-sm-2 mt-3 mt-sm-0">
                <h5 className="fw-semibold mb-0">{SupportLabel.SupportRequest}</h5>
              </div>
            </Row>
            <Row>
              <Widgets data={QueryTicket} />
            </Row>
            <Card>
              <CardHeader className="align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">{SupportLabel.HrSupportRequest}</h4>
              </CardHeader>

              <CardBody>
                {(hrSupportList?.length > 0 || tableLoader) ? (
                  <TableContainer
                    columns={QueryColumns}
                    data={hrSupportList || []}
                    isAddUserList={false}
                    customPageSize={5}
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted"
                    tableLoader={tableLoader}
                  />
                ) : (
                  <NoResultsMessage/>
                )}
              </CardBody>
            </Card>
          </Container>
      </div>
    </React.Fragment>
  );
};

export default EmployeeSupport;
